import { Badge, InlineStack, TabNav, TabNavList, TabNavTab } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import {
  PlatformIcon,
  PLATFORM_LABELS,
  type PushPlatform,
  type PushPlatformDetails,
} from '@/features/push-credentials';
import { PUSH_PLATFORM_ROUTES } from '../route-paths';

const TABS: readonly PushPlatform[] = ['ios', 'android'];

/** The same tab is reachable with or without a trailing slash. */
function withoutTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

export type PushTabNavProps = {
  workspaceId: number | null;
  platforms: readonly PushPlatformDetails[];
  pathname: string;
};

export function PushTabNav({ workspaceId, platforms, pathname }: PushTabNavProps) {
  const router = useRouter();
  const currentPath = withoutTrailingSlash(pathname);

  return (
    <TabNav aria-label="Push notifications">
      <TabNavList>
        {TABS.map((platform) => {
          const href = router.buildLocation({
            to: PUSH_PLATFORM_ROUTES[platform],
            params: { workspaceId: workspaceId ?? ('last' as const) },
          }).href;
          const isEnabled = platforms.some((entry) => entry.key === platform);

          return (
            <TabNavTab
              key={platform}
              href={href}
              isActive={currentPath === withoutTrailingSlash(href)}
              data-test-push-tab={platform}
            >
              <InlineStack gap="150" alignBlock="center">
                <PlatformIcon platform={platform} />
                {PLATFORM_LABELS[platform]}
                {isEnabled ? (
                  <Badge
                    color="success"
                    showStatusIndicator={true}
                    overflow="visible"
                    data-test-push-tab-status-enabled={platform}
                  >
                    Enabled
                  </Badge>
                ) : null}
              </InlineStack>
            </TabNavTab>
          );
        })}
      </TabNavList>
    </TabNav>
  );
}
